import {
  AttachPresaleCodeRequest,
  CreatePresaleCodeRequest,
  DetachPresaleCodeRequest,
  PresaleCodeFiltersType,
  PresaleCodeListItem,
  PresaleCodeWithRulesResponse,
  SetPresaleCodeRulesRequest,
  TixologiResumedEvent,
  UpdatePresaleCodeRequest,
} from "@punchup/data/legacy/tixologi";
import { ShowWithComedian, getShow } from "@punchup/data/legacy/shows";

import dayjs from "dayjs";
import { getTixologiEvents } from "@punchup/data/legacy/tixologi";
import { logException } from "@punchup/sentry";

export const getTixologiEvent = async (
  eventId: string
): Promise<TixologiResumedEvent | null> => {
  const baseUrl = process.env.NEXT_PUBLIC_TIXOLOGI_API_URL;
  if (!baseUrl) return null;

  const response = await fetch(`${baseUrl}/public/ticketing/events/${eventId}`);
  if (!response.ok) return null;
  return response.json();
};

/**
 * Get a Tixologi event using authenticated endpoint
 * Use this for password-protected events or when you need full event data
 * @param eventId - The Tixologi event ID
 * @param token - The Tixologi authentication token
 */
export const getTixologiEventPrivate = async (
  eventId: string,
  token: string
): Promise<TixologiResumedEvent> => {
  const baseUrl = process.env.NEXT_PUBLIC_TIXOLOGI_API_URL;
  if (!baseUrl)
    throw new Error("NEXT_PUBLIC_TIXOLOGI_API_URL is not configured");

  const response = await fetch(`${baseUrl}/events/${eventId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch Tixologi event ${eventId}: ${response.status}`
    );
  }
  return response.json();
};

export { getTixologiEvents };

export const handleEventUpdated = async (
  showId: string,
  eventId: string,
  token: string,
  updateShow: (showId: string, show: ShowWithComedian) => void
) => {
  if (!token) {
    throw new Error("Authentication token is required to update event");
  }
  const [show, event] = await Promise.all([
    getShow(showId),
    getTixologiEventPrivate(eventId, token),
  ]);

  // A show soft-deleted meanwhile reads as null: re-syncing it would save a
  // partial show built from the event alone.
  if (!show) throw new Error(`Show ${showId} not found (deleted?)`);
  if (!event) return;

  if (!event.name || !event.start_date || !event.time_zone) {
    // Bail out instead of corrupting the show with empty/zero values.
    // Most common cause: stale Tixologi token returning a degraded 200 payload.
    logException("Tixologi event-updated sync received incomplete payload", {
      extra: {
        showId,
        eventId,
        hasName: !!event.name,
        hasStartDate: !!event.start_date,
        hasTimeZone: !!event.time_zone,
      },
    });
    return;
  }

  const datetime = dayjs(event.start_date)
    .tz(event.time_zone)
    .format("YYYY-MM-DDTHH:mm");

  const resynced: Partial<ShowWithComedian> = {
    ...show,
    datetime,
    title: event.name,
    metadata_text: event.description,
  };
  updateShow(showId, resynced as ShowWithComedian);
};

export const getTixologiPromotion = async (
  promotionId: string,
  token: string
) => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/promotions/${promotionId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.json();
};

const buildQueryString = (
  params: Record<string, string | number | boolean>
): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
};

export const getTixologiPresaleCodes = async (
  token: string,
  filters?: PresaleCodeFiltersType
): Promise<{
  presale_codes: PresaleCodeListItem[];
  total_count: number;
  page: number;
  per_page: number;
  total_pages: number;
}> => {
  const queryString = filters ? buildQueryString(filters) : "";
  const url = `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/presale-codes${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch presale codes");
  }

  const presale_codes: PresaleCodeListItem[] = await response.json();
  const totalCount = parseInt(response.headers.get("X-Total-Count") || "0", 10);

  // Calculate pagination metadata
  const page = filters?.page || 1;
  const per_page = filters?.per_page || 15;
  const total_pages = Math.ceil(totalCount / per_page);

  return {
    presale_codes,
    total_count: totalCount,
    page,
    per_page,
    total_pages,
  };
};

export const getTixologiPresaleCode = async (
  token: string,
  id: string
): Promise<PresaleCodeListItem> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/presale-codes/${id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch presale code");
  }

  return response.json();
};

/**
 * Extract a human-readable reason from a failed ticketing-API response.
 *
 * The ticketing API serializes handler errors with `ctx.IndentedJSON(status,
 * err.Error())`, so an error body is a bare JSON *string* (e.g. `"presale code
 * already exists"`) rather than an object. We also tolerate an object shape
 * (`{ message }` / `{ error }`) in case a gateway or a future handler wraps it.
 * Returns `null` when nothing readable is present so the caller can fall back to
 * its generic message.
 */
const parseTixologiErrorMessage = async (
  response: Response
): Promise<string | null> => {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return null;
  }

  if (typeof body === "string") {
    return body.trim() || null;
  }
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    const candidate = record.message ?? record.error;
    if (typeof candidate === "string") {
      return candidate.trim() || null;
    }
  }
  return null;
};

export const createTixologiPresaleCode = async (
  token: string,
  data: CreatePresaleCodeRequest
): Promise<PresaleCodeListItem> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/presale-codes`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const message = await parseTixologiErrorMessage(response);
    throw new Error(message ?? "Failed to create presale code");
  }

  return response.json();
};

export const updateTixologiPresaleCode = async (
  token: string,
  id: number,
  data: UpdatePresaleCodeRequest
): Promise<PresaleCodeListItem> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/presale-codes/${id}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to update presale code");
  }

  return response.json();
};

export const deleteTixologiPresaleCode = async (
  token: string,
  id: number
): Promise<void> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/presale-codes/${id}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to delete presale code");
  }
};

export const attachToTixologiPresaleCode = async (
  token: string,
  id: number,
  data: AttachPresaleCodeRequest
): Promise<{ message: string }> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/presale-codes/${id}/attach`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to attach to presale code");
  }

  return response.json();
};

export const detachFromTixologiPresaleCode = async (
  token: string,
  id: number,
  data: DetachPresaleCodeRequest
): Promise<{ message: string }> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/presale-codes/${id}/detach`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to detach from presale code");
  }

  return response.json();
};

export const setTixologiPresaleCodeRules = async (
  token: string,
  id: number,
  data: SetPresaleCodeRulesRequest
): Promise<{ message: string }> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/presale-codes/${id}/rules`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to set presale code rules");
  }

  return response.json();
};

export const getTixologiPresaleCodeWithRules = async (
  token: string,
  id: number
): Promise<PresaleCodeWithRulesResponse> => {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_TIXOLOGI_API_URL}/presale-codes/${id}/rules`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to get presale code rules");
  }

  return response.json();
};
